import React, { useContext, useMemo, useCallback, useEffect } from "react";
import IntegrationCard from "../../integration-card/integration-card";
import { useApi, useMount, useRouter } from "hooks";
import { getMewsIntegrationStatus } from "apis/integration.api";
import { VenueContext } from "contexts";
import { getCloudBedsAccessToken, searchApplications } from "apis";
import { Text, Title } from "components/commons";
import lang from "translations";
// import { useFlags } from "launchdarkly-react-client-sdk";
import { mapIntegrationFlag } from "services/object.service";
import { Path } from "paths";
import IntegrationCardListLoadingState from "./integration-card-list-loading-state";
import { IntegrationType } from "enums";
import { searchApplicationActivations } from "apis/application.api";
import { fetchConnectStatusSiigo } from "apis/siigo.api";
import useFlags from "hooks/useFlags";
import { IntegrationSections } from "../data";
import { isEatappVenue, isOdooVenue } from "services/venue.service";
import styles from "../../integration-setting.module.scss";

// Eat App and Odoo are switched on per venue by the BE (SVEMS-998), not connected from
// this page, so their cards only reflect the venue flags.
const venueIntegrations = {
  "Eat App": isEatappVenue,
  Odoo: isOdooVenue,
};

const IntegrationCardList = ({
  data,
  classname,
  mappedOutlets,
  loadingOutlet,
  loadingOutlets,
  mappedServices,
  outletRequest,
  serviceRequest,
}) => {
  const { venue, setSiigoConnected } = useContext(VenueContext);
  const { search } = window.location;
  const flags = useFlags();
  const cloudbedsQuery = localStorage.getItem("cloudbedsOAuthCode")
    ? new URLSearchParams(localStorage.getItem("cloudbedsOAuthCode"))
    : new URLSearchParams(search);
  const cloudbedsCode = cloudbedsQuery.get("code");
  const { history } = useRouter();

  const mewsRequest = useApi({
    api: getMewsIntegrationStatus,
    params: {
      venueId: venue?.venueId,
    },
  });

  const { request: cloudBedsRequest, loading: cloudbedsConnecting } = useApi({
    api: getCloudBedsAccessToken,
    params: {
      code: cloudbedsCode,
      state: venue?.venueId,
    },
    handleOwnError: true,
  });

  const {
    request: searchApps,
    result: applicationsResult,
    loading,
  } = useApi({
    api: searchApplications,
    params: {
      venueId: venue?.venueId,
    },
    handleOwnError: true,
  });

  const activationsRequest = useApi({
    api: searchApplicationActivations,
    params: {
      venueId: venue?.venueId,
    },
  });

  const siigoRequest = useApi({
    api: fetchConnectStatusSiigo,
    mapper: { _keys: ["integrationSiigoId", "isConnected"] },
    params: { venueId: venue?.venueId },
  });

  const integrationData = useMemo(() => {
    const integrations = {
      Mews: mewsRequest,
      MEWS_Accounting: mewsRequest,
      Cloudbeds: mewsRequest,
      PROPERTY_MANAGEMENT_SYSTEM: activationsRequest,
    };
    const activationResult = mewsRequest?.result?.data
      ?.flatMap((dt) => dt.integrationType)
      .join(",");

    function getAccessToken(integration) {
      const { integrationType = "" } = integration;
      if (integrationType === IntegrationType.CLOUDBEDS) {
        return null;
      }
      if (
        integrationType === IntegrationType.MEWS ||
        integrationType === IntegrationType.MEWS_ACCOUNTING
      ) {
        return mewsRequest?.result?.data?.find(
          (dt) =>
            dt.integrationType.includes(IntegrationType.MEWS_ACCOUNTING) ||
            dt.integrationType.includes(IntegrationType.MEWS),
        )?.accessToken;
      }
      if (integrationType === IntegrationType.PROPERTY_MANAGEMENT_SYSTEM) {
        return activationsRequest?.result?.data?.find((dt) => dt.applicationId === integration.id)
          ?.accessToken;
      }
      return mewsRequest?.result?.data?.find(
        (dt) =>
          dt.integrationType === integrationType || dt.integrationType.includes(integrationType),
      )?.accessToken;
    }

    function getSFTPDetails(integration) {
      const { integrationType = IntegrationType.Purchase_Plus } = integration;

      return {
        hostname: mewsRequest?.result?.data?.find(
          (dt) =>
            dt.integrationType === integrationType || dt.integrationType.includes(integrationType),
        )?.enterpriseName,
        username: mewsRequest?.result?.data?.find(
          (dt) =>
            dt.integrationType === integrationType || dt.integrationType.includes(integrationType),
        )?.enterpriseId,
        password: mewsRequest?.result?.data?.find(
          (dt) =>
            dt.integrationType === integrationType || dt.integrationType.includes(integrationType),
        )?.accessToken,
        directory: mewsRequest?.result?.data?.find(
          (dt) =>
            dt.integrationType === integrationType || dt.integrationType.includes(integrationType),
        )?.integrationId,
      };
    }

    function getLoadingState(name, request) {
      if (name.toUpperCase() === IntegrationType.CLOUDBEDS && cloudbedsCode) {
        return cloudbedsConnecting;
      }
      return request.loading;
    }

    const integrationTypes = activationResult ? activationResult.split(",") : [];
    const applications = applicationsResult?.data?.content || [];

    return data
      .map((d) => {
        if (!d.isApp || d.name !== "Lobby") {
          return d;
        }

        const application = applications.find((app) =>
          d.applicationDisplayNames.includes(app.displayName),
        );

        return application
          ? {
              ...d,
              name: application.displayName,
              description: application.description,
              id: application.id,
              imageLink: application.logoLink || d.imageLink,
              colors: application.colors || d.colors,
              dashboardLink: application.callbackUrl,
              integrationType: application.category,
              isIntegrated: Boolean(application.approvalStatus),
            }
          : null;
      })
      .filter(Boolean)
      .map((d) => {
      const name = d.name.replaceAll(" ", "_");
      const request = !d.isApp ? integrations?.[name] : integrations?.[d.integrationType];
      const integration = request?.result || {};
      const isIntegrated = d.isComingSoon
        ? false
        : d.featureFlag
          ? mapIntegrationFlag(d.featureFlag, flags)
          : true;
      if (request) {
        const currentIntegration = !d.isApp
          ? integration?.data?.find(
              (dt) =>
                dt.integrationType === d.integrationType ||
                (dt.integrationType &&
                  dt.integrationType.split(",").indexOf(d.integrationType) > -1),
            )
          : integration?.data?.find((dt) => dt.applicationId === d.id);

        return {
          ...d,
          isIntegrated,
          isConnected: !d.isApp ? integrationTypes.includes(d.integrationType) : currentIntegration,
          loading: getLoadingState(name, request),
          appId: d.id,
          id: currentIntegration?.id,
          accessToken: getAccessToken(d),
          SFTPDetails: getSFTPDetails(d),
          request: !d.isApp ? mewsRequest : activationsRequest,
          connectedIntegrationsList: integrationTypes,
        };
      }
      if (name === "Siigo") {
        return {
          ...d,
          isIntegrated,
          isConnected: siigoRequest.mappedData.isConnected,
          appId: siigoRequest.mappedData.integrationSiigoId,
        };
      }
      return {
        ...d,
        isIntegrated,
        isConnected: venueIntegrations[d.name] ? venueIntegrations[d.name](venue) : d.isConnected,
      };
      });
  }, [
    data,
    venue,
    mewsRequest,
    activationsRequest,
    flags,
    cloudbedsConnecting,
    cloudbedsCode,
    siigoRequest,
    applicationsResult?.data?.content,
  ]);

  useMount(async () => {
    mewsRequest.request();
    activationsRequest.request();
    siigoRequest.request();
    fetchApps();
    if (cloudbedsCode) {
      await cloudBedsRequest({ code: cloudbedsCode });
      await mewsRequest.request();
      history.push(Path.INTEGRATION_SETTING);
    }
  });

  const fetchApps = useCallback(
    async (params = {}) => {
      const p = {
        page: 1,
        pageSize: 10,
        ...params,
      };

      return await searchApps({
        page: p.page,
        pageSize: p.pageSize,
      });
    },
    [searchApps],
  );

  useEffect(() => {
    setSiigoConnected(!!siigoRequest.mappedData.isConnected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siigoRequest.mappedData]);

  return !loading ? (
    <div className={styles.integrationSections}>
      {Object.values(IntegrationSections).map((section) => {
        const sectionData = integrationData.filter((item) => item.section === section);

        if (!sectionData.length) {
          return null;
        }

        return (
          <section key={section} className={styles.integrationSection}>
            <h2 className={styles.integrationSectionTitle}>{section}</h2>
            <div className={styles.integrationGrid}>
              {sectionData.map((d) => (
                <IntegrationCard
                  key={d.name}
                  data={d}
                  className={classname}
                  refreshList={fetchApps}
                  mappedOutlets={mappedOutlets}
                  loadingOutlets={loadingOutlets || loadingOutlet}
                  mappedServices={mappedServices}
                  onMewsRefresh={() => {
                    mewsRequest.request();
                  }}
                  outletRequest={outletRequest}
                  serviceRequest={serviceRequest}
                  siigoRequest={siigoRequest.request}
                />
              ))}
            </div>
          </section>
        );
      })}
      <section className={styles.integrationSection}>
        <h2 className={styles.integrationSectionTitle}>{IntegrationSections.CUSTOM}</h2>
        <div className={styles.customIntegration}>
          <span className={styles.customIntegrationIcon}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="4" y="4" width="7" height="7" rx="1.6" />
              <rect x="13" y="4" width="7" height="7" rx="1.6" />
              <rect x="4" y="13" width="7" height="7" rx="1.6" />
              <path d="M16.5 14v5" />
              <path d="M14 16.5h5" />
            </svg>
          </span>
          <div className={styles.customIntegrationContent}>
            <Title>{lang.needSomethingElse}</Title>
            <Text>{lang.weBuildCustomIntegrations}</Text>
          </div>
          <a className={styles.customIntegrationButton} href="mailto:support@socashless.com">
            {lang.contactUsAction}
          </a>
        </div>
      </section>
    </div>
  ) : (
    <IntegrationCardListLoadingState loading />
  );
};

export default IntegrationCardList;
